import { getPluginHealthUrl } from './config'

const PROBE_TIMEOUT_MS = 1500

/**
 * Detects whether the DCC plugin for `pluginKey` is currently listening on its
 * own local port (PLUGIN_PORTS in config.js), without consuming the plugin's
 * single WebSocket client slot (so a probe never disturbs an in-flight send).
 *
 * Two-step probe against that port's `/health`:
 *   1. An opaque `no-cors` GET as a liveness check. `no-cors` never triggers
 *      a CORS console error, so when no plugin is running (the common case)
 *      the probe stays completely silent.
 *   2. Only when something answered, a readable GET for identity. `/health`
 *      responds with JSON `{ status: 'ok', dcc: '<key>' }`, where `dcc` is the
 *      running plugin's key — one of `max | blender | unity | unreal | maya` —
 *      confirming the expected plugin (and not some other local process) owns
 *      the port. Against plugin builds that do
 *      not send `Access-Control-Allow-Origin` yet, the browser blocks this
 *      response and logs a CORS error itself — that log cannot be suppressed
 *      from JS and disappears once the plugin ships the header; the probe
 *      then reports `null` (identity unknown).
 *
 * `onResult` receives the plugin key (trimmed, lowercased), or `null` when
 * nothing is running, the request times out, or the payload is unreadable,
 * not `status: 'ok'`, or missing `dcc`.
 *
 * Why native `fetch` and not the project's axios-based `useApiGet` helper:
 *   1. `useApiGet` calls `captureException` on every error. "Plugin not
 *      running" is the *expected* steady state when no DCC plugin is
 *      installed; routing it through Sentry would generate a flood of false
 *      positives on every popover open.
 *   2. `useApiGet` attaches our CSRF token and `withCredentials: true` to
 *      every request. Both are meaningless against a `localhost` plugin port
 *      and represent a small unnecessary leak of session context to whatever
 *      process happens to be bound to that port.
 *   3. Axios runs on top of XHR, which has no equivalent of fetch's
 *      `mode: 'no-cors'`, so the liveness step could not stay console-silent.
 *   4. `useApiGet` is a React hook and exposes loading state for UI; this
 *      probe is a fire-and-forget background check with no spinner.
 *
 * @param {string} pluginKey
 * @param {(pluginKey: string | null) => void} onResult
 * @returns {{ close: () => void }}
 */
// The identity request is readable (cors-mode), which the browser hard-blocks
// WITH a console error when the running plugin build does not send
// `Access-Control-Allow-Origin`. That log is browser-emitted and cannot be
// suppressed from JS, so we keep it to at most one line per port per page
// load: concurrent probes of the same port share a single in-flight identity
// request, and after the first block we stop re-attempting
// identity on that port for the rest of the page. Cleared on reload, so
// upgrading the plugin mid-session just needs a refresh.
const identityBlockedThisPage = new Set()
const inFlightIdentity = new Map()

export const resetIdentityBlockForTests = () => {
  identityBlockedThisPage.clear()
  inFlightIdentity.clear()
}

// Resolves with the running plugin's key (trimmed, lowercased) or null. Not
// tied to any single probe's abort signal - the request is shared, local, and
// cheap, and each caller still enforces its own timeout via settle().
const fetchIdentity = (pluginKey) => {
  if (!inFlightIdentity.has(pluginKey)) {
    inFlightIdentity.set(
      pluginKey,
      fetch(getPluginHealthUrl(pluginKey), { method: 'GET', cache: 'no-store' })
        .then(async (res) => {
          if (!res.ok) return null
          try {
            const { status, dcc } = await res.json()
            if (status !== 'ok' || typeof dcc !== 'string') return null
            return dcc.trim().toLowerCase() || null
          } catch {
            return null
          }
        })
        .catch(() => {
          // A rejected readable request against a server the opaque liveness
          // check just reached means the response was CORS-blocked (not a
          // network failure).
          identityBlockedThisPage.add(pluginKey)
          return null
        })
        .finally(() => {
          inFlightIdentity.delete(pluginKey)
        }),
    )
  }
  return inFlightIdentity.get(pluginKey)
}

export const probePlugin = (pluginKey, onResult) => {
  let settled = false
  let timeoutId
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const signal = controller ? controller.signal : undefined

  const settle = (resultKey) => {
    if (settled) return
    settled = true
    if (timeoutId) clearTimeout(timeoutId)
    onResult(resultKey)
  }

  timeoutId = setTimeout(() => {
    if (controller) controller.abort()
    settle(null)
  }, PROBE_TIMEOUT_MS)

  const readIdentity = () => {
    if (identityBlockedThisPage.has(pluginKey)) {
      settle(null)
      return
    }
    fetchIdentity(pluginKey).then(settle)
  }

  try {
    fetch(getPluginHealthUrl(pluginKey), { method: 'GET', mode: 'no-cors', cache: 'no-store', signal })
      .then(readIdentity)
      .catch(() => settle(null))
  } catch {
    settle(null)
  }

  return {
    close: () => {
      if (controller) controller.abort()
      settle(null)
    },
  }
}
